import { Autocomplete, Box, Button, DialogContent, TextField, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import { UpsertAction } from "../Enums";
import { DevelopmentItem } from "../models/dtos";
import { useErrorDisplay } from '../components/ErrorDisplay';
import BusySpinner from "../components/BusySpinner";
import { ExtractForSetError, GetUpsertIcon, IsEmpty, IsNullOrUndefined } from "../Helpers";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import I18NText from "../components/I18NText";
import { I18N } from "../I18N";
import DevelopmentClient, { DevelopmentItemErrors } from "../apiClients/DevelopmentClient";
import TalentClient from "../apiClients/TalentClient";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import '@mui/lab';
import { AdapterDayjs as DateAdapter } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import ConditionalDisplay from "../components/ConditionalDisplay";
import { MobileFormStack, ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";

export interface AutocompleteOption {
    label: string;
    id: string;
}

export interface DevelopmentItemUpsertPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    title?: string;
    talentId?: string;
    developmentItemId?: string;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    icon?: JSX.Element;
}

const DevelopmentItemUpsertPopup = ({ mode, title = I18N.Translate("Development Items"), talentId, developmentItemId, onChange = async () => { }, onAdd = async () => { }, icon = GetUpsertIcon(mode), ...btnProps }: DevelopmentItemUpsertPopupProps) => {

    const defaultItem = DevelopmentClient.CreateDefault();

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);

    const initialized = useRef<boolean>();
    const theme = useTheme();

    const [developmentItem, setDevelopmentItem] = useState<DevelopmentItem>(defaultItem);

    const [proficiencies, setProficiencies] = useState<AutocompleteOption[]>([]);
    const [selectedProficiency, setSelectedProficiency] = useState<AutocompleteOption>(null)
    const [localStartDate, setLocalStartDate] = useState<dayjs.Dayjs>(dayjs());

    const loadData = async () => {
        try {
            setBusy(true);

            // Get Proficiencies
            const apiResponse = await new TalentClient().GetOngoingAssessment(talentId);
            if(ExtractForSetError(apiResponse, DevelopmentItemErrors.GetSingle, setError))
                return;

            // if (!shouldContinueSetErrorInfo(apiResponse, DevelopmentItemErrors.GetSingle))
            //     return;

            const p = apiResponse.Content.Proficiencies;

            let options: AutocompleteOption[] = [];
            p.forEach(p => options.push({ label: p.Name, id: p.ProficiencyId }))
            setProficiencies(options);

            if (mode === UpsertAction.Update || mode === UpsertAction.View) {
                const apiResponse = await new DevelopmentClient().GetSingle(developmentItemId);
                if(ExtractForSetError(apiResponse, DevelopmentItemErrors.GetSingle, setError))
                return;

                setDevelopmentItem(apiResponse.Content);
            }

        }
        catch (error) {
            setError(error, DevelopmentItemErrors.GetSingle);
        }
        finally {
            initialized.current = true;
            setBusy(false);
        }
    };

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    };

    const handleClose = async () => {
        setDevelopmentItem(defaultItem);
        setSelectedProficiency(null);
        setOpen(false);
    };

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                if (IsNullOrUndefined(selectedProficiency) || IsEmpty(selectedProficiency.id))
                    throw new Error("Please select a Proficiency")

                let createItem = {
                    TalentId: talentId,
                    ProficiencyId: selectedProficiency.id,
                    Title: developmentItem.Title,
                    Narrative: developmentItem.Narrative,
                    StartDate: developmentItem.StartDate
                }

                const apiResponse = await new DevelopmentClient().Create(createItem);
                if(ExtractForSetError(apiResponse, DevelopmentItemErrors.GetSingle, setError))
                return;

                handleClose();
                onAdd(apiResponse.Content);
            }

        } catch (error) {
            setError(error, DevelopmentItemErrors.Create);
        }
        finally {
            setBusy(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target
        setDevelopmentItem({ ...developmentItem, [name]: value })
    };

    const handleDateInputChange = (name, value: dayjs.Dayjs) => {
        setDevelopmentItem({ ...developmentItem, [name]: value })
        setLocalStartDate(value);
    };

    return (
        <>
            <ErrorDisplay />
            <IconButtonEx disabled={busy} hoverContent={title} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>
            <ResponsiveDialog aria-labelledby="development-item-upsert-title" open={open} fullWidth tabletFullScreen="portrait" PaperProps={{ sx: { overflow: 'hidden', '& > div': { flex: '1 1 auto', minHeight: 0, height: 'auto !important', display: 'flex', flexDirection: 'column' } } }}>
                <ResponsiveDialogHeader id="development-item-upsert-title" title={<>
                    <ConditionalDisplay ifTrue={mode === UpsertAction.Create}>
                        <I18NText>Create Development Plan Item</I18NText>
                    </ConditionalDisplay>
                    <ConditionalDisplay ifTrue={mode === UpsertAction.Update}>
                        <I18NText>Update Development Plan Item</I18NText>
                    </ConditionalDisplay>
                    <ConditionalDisplay ifTrue={mode === UpsertAction.View}>
                        <I18NText>Development Item</I18NText>
                    </ConditionalDisplay>
                </>} />
                <BusySpinner busy={busy}>
                    <DialogContent dividers sx={{ overflowY: 'auto' }}>
                        <MobileFormStack tabletColumns={2} desktopColumns={1} sx={{ gap: 2 }}>
                            <Autocomplete
                                id="proficiency-autocomplete"
                                options={proficiencies}
                                value={selectedProficiency}
                                onChange={(e, value) => setSelectedProficiency(value as AutocompleteOption)}
                                renderInput={(params) => <TextField {...params} label={I18N.Translate("Proficiency")} />} />
                            <TextField label={I18N.Translate("Title")} fullWidth
                                name="Title"
                                size="small"
                                variant="outlined"
                                error={IsEmpty(developmentItem?.Title)}
                                value={developmentItem?.Title ?? ''}
                                onChange={handleInputChange} />
                            <TextField label="Narrative" fullWidth
                                name={I18N.Translate("Narrative")}
                                size="small"
                                variant="outlined"
                                multiline
                                rows={8}
                                error={IsEmpty(developmentItem?.Narrative)}
                                value={developmentItem?.Narrative ?? ''}
                                onChange={handleInputChange} />
                            <Box sx={{ marginTop: '1em' }}>
                                <LocalizationProvider dateAdapter={DateAdapter}>
                                <DatePicker
                                    label={I18N.Translate("Start Date")}
                                    disabled={mode === UpsertAction.View}
                                    value={localStartDate}
                                    minDate={dayjs()}
                                    onChange={(newValue) => handleDateInputChange('StartDate', newValue)}        
                                    renderInput={(params) => 
                                        <TextField 
                                            {...params} 
                                            fullWidth 
                                            required 
                                            error={!localStartDate}
                                        />
                                    }
                                />
                                </LocalizationProvider>
                            </Box>
                        </MobileFormStack>
                    </DialogContent>
                    <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                        <Button onClick={handleClose} variant='text' color='inherit'>Cancel</Button>
                        <Button disabled={!IsEmpty(developmentItem?.Title) || IsEmpty(developmentItem?.Narrative) || IsEmpty(localStartDate)} onClick={handleSubmit} variant='outlined' color='primary'>Submit</Button>
                    </StickyDialogActions>
                </BusySpinner>
            </ResponsiveDialog>
        </>
    )
};

export default DevelopmentItemUpsertPopup;
